import React, { useState, useEffect, useRef } from 'react';
import { employeeWalletAPI, default as walletApi } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Send, Building2, User, Loader2, CheckCircle, Search, ChevronRight, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => `₦${Math.round(Number(n || 0)).toLocaleString()}`;
const generateIdemKey = () => `PBL-IDEM-FE-${Date.now()}-${Math.random().toString(36).slice(2,10).toUpperCase()}`;

export default function SendMoney() {
  const [step, setStep] = useState('form'); // form, confirm, pin, processing, success, failed
  const [type, setType] = useState('bank_account');
  const [banks, setBanks] = useState([]);
  const [saved, setSaved] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ bank_name: '', bank_code: '', account_number: '', account_name: '', amount: '', narration: '', save_recipient: false, name_enq_ref: '', recipient_email: '', recipient_wallet_id: '' });
  const [bankSearch, setBankSearch] = useState('');
  const [showBankList, setShowBankList] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpMethod, setOtpMethod] = useState('');
  const [resolvingPebbles, setResolvingPebbles] = useState(false);
  const [pebbleQuery, setPebbleQuery] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [result, setResult] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const navigate = useNavigate();
  const idemKeyRef = useRef(generateIdemKey());

  useEffect(() => {
    const load = async () => {
      try {
        const [b, s, w] = await Promise.all([
          employeeWalletAPI.getBanks(),
          employeeWalletAPI.getSavedRecipients(),
          employeeWalletAPI.getBalance(),
        ]);
        setBanks(b); setSaved(s); setWallet(w);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const filteredBanks = banks.filter(b => (b.name || b.bankName || '').toLowerCase().includes(bankSearch.toLowerCase()));
  const fee = type === 'bank_account' ? 10 : 0;
  const amount = parseFloat(form.amount) || 0;
  const total = amount + fee;

  const selectBank = (bank) => {
    setForm({ ...form, bank_name: bank.name || bank.bankName, bank_code: bank.code || bank.bankCode, account_name: '', name_enq_ref: '' });
    setShowBankList(false);
    setBankSearch('');
  };

  const resolveAccount = async () => {
    if (form.account_number.length < 10 || !form.bank_code) return;
    setResolving(true);
    try {
      const res = await employeeWalletAPI.resolveAccount({ bank_code: form.bank_code, account_number: form.account_number });
      setForm({ ...form, account_name: res.account_name, name_enq_ref: res.name_enq_ref || '' });
    } catch { toast.error('Could not resolve account'); }
    finally { setResolving(false); }
  };

  const resolvePebblesUser = async (query) => {
    if (!query || query.length < 3) return;
    setResolvingPebbles(true);
    try {
      const res = await walletApi.post('/employee/resolve-pebbles-user', { account_number: query, email: query });
      setForm(f => ({ ...f, account_name: res.data.account_name, account_number: res.data.account_number || '', recipient_email: res.data.email || '', recipient_wallet_id: res.data.wallet_id || '' }));
    } catch (err) {
      setForm(f => ({ ...f, account_name: '', recipient_email: '', recipient_wallet_id: '' }));
      toast.error(err?.response?.data?.detail || 'Pebbles user not found');
    }
    finally { setResolvingPebbles(false); }
  };

  useEffect(() => {
    if (form.account_number.length === 10 && form.bank_code) resolveAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.account_number, form.bank_code]);

  const handleUseSaved = (r) => {
    const bank = banks.find(b => (b.name || b.bankName) === r.bank_name);
    setForm({ ...form, bank_name: r.bank_name, bank_code: bank?.code || bank?.bankCode || '', account_number: r.account_number, account_name: r.name });
  };

  const handleRequestOtp = async () => {
    setSendingOtp(true);
    try {
      const res = await employeeWalletAPI.sendTransferOtp();
      setOtpMethod(res.method || 'email');
      toast.success(res.message || 'Verification code sent');
    } catch { toast.error('Failed to send OTP'); }
    finally { setSendingOtp(false); }
  };

  const handleSend = async () => {
    if (!otp || otp.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setSending(true);
    try {
      const res = await employeeWalletAPI.sendMoney({
        recipient_type: type, amount, otp,
        bank_name: form.bank_name, bank_code: form.bank_code,
        account_number: form.account_number, account_name: form.account_name,
        name_enq_ref: form.name_enq_ref,
        recipient_email: form.recipient_email,
        narration: form.narration, save_recipient: form.save_recipient,
      }, idemKeyRef.current);
      setResult(res);
      // For bank transfers with processing/review status, show processing screen
      if (res.status === 'processing' || res.status === 'review') {
        setStep('processing');
        setPollCount(0);
      } else if (res.status === 'completed') {
        setStep('success');
      } else {
        setStep('processing');
        setPollCount(0);
      }
    } catch (e) {
      // If the response still has a reference, the transaction was created —
      // show the processing screen instead of an error toast
      const errData = e.response?.data;
      if (errData?.reference) {
        setResult(errData);
        setStep('processing');
        setPollCount(0);
        return;
      }
      if (e.response?.status === 409) {
        toast.error('This transfer is already being processed. Please wait.');
      } else if (e.response?.status === 502) {
        toast.error(e.response?.data?.detail || 'Banking service is currently unreachable. Your funds have been returned.');
      } else {
        toast.error(e.response?.data?.detail || 'Transfer failed');
      }
    }
    finally { setSending(false); }
  };

  // Poll transfer status when in processing state
  useEffect(() => {
    if (step !== 'processing' || !result?.reference) return;
    if (pollCount >= 12) return; // Stop after 60 seconds (12 * 5s)
    
    const timer = setTimeout(async () => {
      try {
        const statusRes = await walletApi.get(`/employee/transfer/status/${result.reference}`);
        const data = statusRes.data;
        if (data.status === 'completed') {
          setResult(prev => ({ ...prev, status: 'completed', new_balance: data.new_balance, timeline: data.timeline }));
          setStep('success');
          return;
        }
        if (data.status === 'failed') {
          setResult(prev => ({ ...prev, status: 'failed', new_balance: data.new_balance, failure_reason: data.failure_reason, timeline: data.timeline }));
          setStep('failed');
          return;
        }
        // Still processing — update timeline
        setResult(prev => ({ ...prev, timeline: data.timeline }));
        setPollCount(c => c + 1);
      } catch {
        setPollCount(c => c + 1);
      }
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [step, pollCount, result?.reference]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;

  // PROCESSING SCREEN with 3-step progress
  if (step === 'processing' && result) {
    const timeline = result.timeline || [];
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4" data-testid="send-processing">
        <p className="text-lg font-bold text-gray-900 mb-1">Transfer Processing</p>
        <p className="text-sm text-gray-500 mb-6 text-center">
          Your transfer of {fmt(result.amount)} to {result.recipient_name}
        </p>

        {/* 3-step progress indicator */}
        <div className="w-full max-w-sm space-y-0 mb-6" data-testid="transfer-progress-steps">
          {(timeline.length > 0 ? timeline : [
            { label: 'Transfer submitted — your account has been debited', done: true },
            { label: 'Sent to recipient\'s bank', done: true },
            { label: 'Waiting for bank confirmation...', done: false },
          ]).map((s, i, arr) => (
            <div key={i} className="flex items-start gap-3 relative" data-testid={`progress-step-${i}`}>
              {/* Vertical connector line */}
              {i < arr.length - 1 && (
                <div className={`absolute left-[13px] top-[26px] w-0.5 h-[calc(100%-6px)] ${s.done ? 'bg-emerald-300' : 'bg-gray-200'}`} />
              )}
              {/* Node */}
              <div className={`w-[26px] h-[26px] rounded-full shrink-0 flex items-center justify-center z-10 ${
                s.failed ? 'bg-red-500' : s.done ? 'bg-emerald-500' : 'bg-gray-200'
              }`}>
                {s.failed ? (
                  <span className="text-white text-xs font-bold">!</span>
                ) : s.done ? (
                  <CheckCircle className="w-3.5 h-3.5 text-white" />
                ) : (
                  <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin" />
                )}
              </div>
              {/* Label */}
              <div className="pt-0.5 pb-4">
                <p className={`text-sm ${s.done ? (s.failed ? 'text-red-600 font-medium' : 'text-gray-900') : 'text-gray-400'}`}>{s.label}</p>
                {s.time && s.time !== 'Waiting...' && <p className="text-[10px] text-gray-400">{s.time}</p>}
              </div>
            </div>
          ))}
        </div>

        <Card className="w-full max-w-sm border-gray-200">
          <CardContent className="p-4 space-y-2 text-xs">
            <div className="flex justify-between"><span className="text-gray-500">Reference</span><span className="font-mono text-gray-900">{result.reference}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Amount</span><span className="text-gray-900">{fmt(result.amount)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Fee</span><span className="text-gray-900">{fmt(result.fee)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Balance</span><span className="font-bold text-gray-900">{fmt(result.new_balance)}</span></div>
          </CardContent>
        </Card>

        <p className="text-xs text-gray-400 mt-4 text-center">
          {pollCount < 12 ? "We'll notify you when it completes." : "Your transfer is still processing. Check your transaction history for updates."}
        </p>
        <Button className="mt-4 bg-gray-900 hover:bg-gray-800 text-white" onClick={() => navigate('/app/employee/wallet')} data-testid="done-btn">
          Done
        </Button>
      </div>
    );
  }

  // FAILED SCREEN (after webhook confirms failure)
  if (step === 'failed' && result) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4" data-testid="send-failed">
        <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
          <XCircle className="w-8 h-8 text-red-500" />
        </div>
        <p className="text-lg font-bold text-gray-900 mb-1">Transfer Failed</p>
        <p className="text-sm text-gray-500 mb-2 text-center">
          Your funds of {fmt(result.total || result.amount)} have been returned to your wallet.
        </p>
        {result.failure_reason && (
          <p className="text-xs text-red-500 bg-red-50 px-3 py-1 rounded-full mb-4 text-center">{result.failure_reason}</p>
        )}
        <Card className="w-full max-w-sm border-gray-200">
          <CardContent className="p-4 space-y-2 text-xs">
            <div className="flex justify-between"><span className="text-gray-500">Reference</span><span className="font-mono text-gray-900">{result.reference}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Updated Balance</span><span className="font-bold text-gray-900">{fmt(result.new_balance)}</span></div>
          </CardContent>
        </Card>
        <div className="flex gap-2 mt-6">
          <Button variant="outline" onClick={() => { setStep('form'); setResult(null); setOtp(''); setOtpMethod(''); idemKeyRef.current = generateIdemKey(); }} data-testid="try-again-btn">Try Again</Button>
          <Button className="bg-gray-900 hover:bg-gray-800 text-white" onClick={() => navigate('/app/employee/wallet')}>Done</Button>
        </div>
      </div>
    );
  }

  // SUCCESS SCREEN
  if (step === 'success' && result) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4" data-testid="send-success">
        <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
          <CheckCircle className="w-8 h-8 text-emerald-500" />
        </div>
        <p className="text-2xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'JetBrains Mono' }}>{fmt(result.amount)}</p>
        <p className="text-sm text-gray-500 mb-6">sent to {result.recipient_name}</p>
        <Card className="w-full max-w-sm border-gray-200">
          <CardContent className="p-4 space-y-2 text-xs">
            <div className="flex justify-between"><span className="text-gray-500">Reference</span><span className="font-mono text-gray-900">{result.reference}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Date</span><span className="text-gray-900">{new Date(result.timestamp).toLocaleString()}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Fee</span><span className="text-gray-900">{fmt(result.fee)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">New Balance</span><span className="font-bold text-gray-900">{fmt(result.new_balance)}</span></div>
          </CardContent>
        </Card>
        <div className="flex gap-2 mt-6">
          <Button variant="outline" onClick={() => { setStep('form'); setForm({ bank_name: '', bank_code: '', account_number: '', account_name: '', amount: '', narration: '', save_recipient: false, name_enq_ref: '' }); setOtp(''); setOtpMethod(''); idemKeyRef.current = generateIdemKey(); }}>Send Again</Button>
          <Button className="bg-pink-500 hover:bg-pink-600 text-white" onClick={() => navigate('/app/employee/wallet')}>Done</Button>
        </div>
      </div>
    );
  }

  // CONFIRM / PIN
  if (step === 'confirm') {
    return (
      <div className="max-w-md mx-auto space-y-5" data-testid="send-confirm">
        <button onClick={() => setStep('form')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h2 className="text-lg font-bold text-gray-900">Confirm Transfer</h2>
        <Card className="border-gray-200">
          <CardContent className="p-4 space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">To</span><span className="font-medium text-gray-900">{form.account_name}</span></div>
            {type === 'bank_account' && <div className="flex justify-between"><span className="text-gray-500">Bank</span><span className="text-gray-900">{form.bank_name} • {form.account_number}</span></div>}
            <div className="flex justify-between"><span className="text-gray-500">Amount</span><span className="font-bold text-gray-900">{fmt(amount)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Fee</span><span className="text-gray-900">{fmt(fee)}</span></div>
            <div className="flex justify-between border-t pt-2"><span className="font-semibold text-gray-900">Total</span><span className="font-bold text-gray-900">{fmt(total)}</span></div>
            {form.narration && <div className="flex justify-between"><span className="text-gray-500">Narration</span><span className="text-gray-900">{form.narration}</span></div>}
          </CardContent>
        </Card>
        <div>
          {!otpMethod ? (
            <Button className="w-full bg-gray-900 hover:bg-gray-800 text-white h-10 text-xs" disabled={sendingOtp} onClick={handleRequestOtp} data-testid="request-otp-btn">
              {sendingOtp ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Send verification code to email
            </Button>
          ) : (
            <>
              <Label className="text-sm font-medium text-gray-700">Enter 6-digit code sent to your email</Label>
              <div className="flex gap-2 mt-2 justify-center">
                {[0,1,2,3,4,5].map(i => (
                  <input key={i} type="text" inputMode="numeric" maxLength={1} className="w-10 h-12 text-center text-lg font-bold border border-gray-300 rounded-lg focus:border-pink-500 focus:ring-1 focus:ring-pink-500 outline-none"
                    value={otp[i] || ''} onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9]/g, '');
                      const newOtp = otp.slice(0, i) + v + otp.slice(i + 1);
                      setOtp(newOtp);
                      if (v && i < 5) e.target.nextSibling?.focus();
                    }}
                    data-testid={`otp-digit-${i}`}
                  />
                ))}
              </div>
              <button onClick={handleRequestOtp} disabled={sendingOtp} className="text-xs text-pink-500 hover:text-pink-600 mt-2 block mx-auto">
                {sendingOtp ? 'Sending...' : 'Resend code'}
              </button>
            </>
          )}
        </div>
        {otpMethod && (
          <Button className="w-full bg-pink-500 hover:bg-pink-600 text-white h-11" disabled={otp.length < 6 || sending} onClick={handleSend} data-testid="confirm-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : `Send ${fmt(amount)}`}
          </Button>
        )}
      </div>
    );
  }

  // MAIN FORM
  return (
    <div className="max-w-md mx-auto space-y-5" data-testid="send-money-form">
      <button onClick={() => navigate('/app/employee/wallet')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft className="w-4 h-4" /> Send Money
      </button>
      <p className="text-xs text-gray-500">Available: <span className="font-bold text-gray-900">{fmt(wallet?.available_balance ?? wallet?.balance)}</span></p>

      {/* Transfer Type */}
      <div className="flex gap-2">
        {[
          { id: 'bank_account', icon: Building2, label: 'Bank Account', sub: 'standard transfer' },
          { id: 'pebbles_user', icon: User, label: 'Pebbles User', sub: 'instant, free' },
        ].map(t => (
          <button key={t.id} onClick={() => { setType(t.id); setForm(f => ({ ...f, account_name: '', account_number: '', bank_name: '', bank_code: '', name_enq_ref: '', recipient_email: '', recipient_wallet_id: '' })); setPebbleQuery(''); }}
            className={`flex-1 flex items-center gap-2.5 p-3 rounded-xl border-2 transition-colors ${type === t.id ? 'border-pink-500 bg-pink-50' : 'border-gray-200 hover:border-gray-300'}`}>
            <t.icon className={`w-4 h-4 ${type === t.id ? 'text-pink-500' : 'text-gray-400'}`} />
            <div className="text-left">
              <p className={`text-xs font-medium ${type === t.id ? 'text-pink-700' : 'text-gray-700'}`}>{t.label}</p>
              <p className="text-[10px] text-gray-400">{t.sub}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Bank Selection */}
      {type === 'bank_account' && (
        <div className="space-y-3">
          <div className="relative">
            <Label className="text-xs font-medium text-gray-600">Bank</Label>
            <button onClick={() => setShowBankList(!showBankList)}
              className="w-full mt-1 flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg text-sm hover:border-gray-400">
              <span className={form.bank_name ? 'text-gray-900' : 'text-gray-400'}>{form.bank_name || 'Select bank'}</span>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </button>
            {showBankList && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <div className="p-2 sticky top-0 bg-white border-b">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input placeholder="Search banks..." value={bankSearch} onChange={e => setBankSearch(e.target.value)} className="h-8 pl-8 text-xs" />
                  </div>
                </div>
                {filteredBanks.map(b => (
                  <button key={b.code || b.bankCode} onClick={() => selectBank(b)} className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 text-gray-700">{b.name || b.bankName}</button>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs font-medium text-gray-600">Account Number</Label>
            <Input value={form.account_number} onChange={e => setForm({ ...form, account_number: e.target.value.replace(/\D/g, '').slice(0, 10) })}
              placeholder="0123456789" className="h-9 mt-1" maxLength={10} data-testid="account-number-input" />
          </div>
          {resolving && <p className="text-xs text-gray-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Verifying account...</p>}
          {form.account_name && <p className="text-xs text-emerald-600 font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" /> {form.account_name}</p>}
        </div>
      )}

      {/* Pebbles User Lookup */}
      {type === 'pebbles_user' && (
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-medium text-gray-600">Account Number or Email</Label>
            <div className="flex gap-2 mt-1">
              <Input value={pebbleQuery} onChange={e => setPebbleQuery(e.target.value)}
                placeholder="Enter virtual account number or email"
                className="h-9 flex-1" data-testid="pebbles-user-input" />
              <Button size="sm" variant="outline" className="h-9 px-3"
                disabled={resolvingPebbles || pebbleQuery.length < 3}
                onClick={() => resolvePebblesUser(pebbleQuery)} data-testid="pebbles-resolve-btn">
                {resolvingPebbles ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>
          {resolvingPebbles && <p className="text-xs text-gray-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Looking up user...</p>}
          {form.account_name && type === 'pebbles_user' && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-xs text-emerald-700 font-medium flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" /> {form.account_name}</p>
              {form.recipient_email && <p className="text-[10px] text-emerald-600 mt-0.5">{form.recipient_email}</p>}
            </div>
          )}
        </div>
      )}

      {/* Amount */}
      <div>
        <Label className="text-xs font-medium text-gray-600">Amount</Label>
        <div className="relative mt-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">₦</span>
          <Input type="text" inputMode="numeric" value={form.amount} data-testid="amount-input"
            onChange={e => setForm({ ...form, amount: e.target.value.replace(/[^0-9]/g, '') })}
            placeholder="0" className="h-10 pl-7 text-lg font-bold" style={{ fontFamily: 'JetBrains Mono' }} />
        </div>
        {fee > 0 && amount > 0 && <p className="text-[10px] text-gray-400 mt-1">Transfer fee: {fmt(fee)} • Total: {fmt(total)}</p>}
      </div>

      {/* Narration */}
      <div>
        <Label className="text-xs font-medium text-gray-600">Narration (optional)</Label>
        <Input value={form.narration} onChange={e => setForm({ ...form, narration: e.target.value })} placeholder="e.g. February rent" className="h-9 mt-1" />
      </div>

      {/* Saved Recipients */}
      {type === 'bank_account' && saved.length > 0 && (
        <div>
          <Label className="text-xs font-medium text-gray-600 mb-2 block">Saved Recipients</Label>
          <div className="space-y-1.5">
            {saved.map(r => (
              <button key={r.id} onClick={() => handleUseSaved(r)} className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-gray-200 hover:border-pink-300 hover:bg-pink-50 transition-colors">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-600">
                    {r.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="text-left">
                    <p className="text-xs font-medium text-gray-900">{r.name}</p>
                    <p className="text-[10px] text-gray-400">{r.bank_name} • ****{r.account_number.slice(-4)}</p>
                  </div>
                </div>
                <span className="text-[10px] text-pink-500 font-medium">Use</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {type === 'bank_account' && (
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={form.save_recipient} onChange={e => setForm({ ...form, save_recipient: e.target.checked })} id="save" className="rounded border-gray-300" />
          <label htmlFor="save" className="text-xs text-gray-500">Save this recipient</label>
        </div>
      )}

      <Button className="w-full bg-pink-500 hover:bg-pink-600 text-white h-11" data-testid="send-btn"
        disabled={!form.account_name || !amount || amount > (wallet?.available_balance ?? wallet?.balance ?? 0)}
        onClick={() => setStep('confirm')}>
        <Send className="w-4 h-4 mr-2" /> Send {amount > 0 ? fmt(amount) : ''}
      </Button>
    </div>
  );
}
